import { isSsoSignInErrorRef } from "@repo/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/_client";
import {
  confirmSsoProviderIdChange,
  deleteSsoProvider,
  getSsoProvider,
  getSsoSignInError,
  startSsoCallbackTest,
  testSsoConnection,
  updateSsoProvider,
} from "@/lib/api/sso";

export function useSsoProvider() {
  const { data, isLoading } = useQuery({
    queryKey: ["sso-provider"],
    queryFn: getSsoProvider,
  });
  return { settings: data ?? null, provider: data?.provider ?? null, isLoading };
}

export function useUpdateSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateSsoProvider,
    // onSettled: a lost compare-and-set (409) must refresh the form too.
    onSettled: () => qc.invalidateQueries({ queryKey: ["sso-provider"] }),
  });
}

/** Starts a test sign-in; the caller sends the browser to the returned authorization URL. */
export function useStartSsoCallbackTest() {
  return useMutation({ mutationFn: startSsoCallbackTest });
}

export function useConfirmSsoProviderIdChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: confirmSsoProviderIdChange,
    onSuccess: (data) => {
      qc.setQueryData(["sso-provider"], data);
      // The bootstrap carries the pending change behind the sign-in heads-up
      qc.invalidateQueries({ queryKey: ["app-bootstrap"] });
    },
  });
}

export function useDeleteSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSsoProvider,
    // Refresh even when the delete fails, so the form shows what is really stored.
    onSettled: () => qc.invalidateQueries({ queryKey: ["sso-provider"] }),
  });
}

export function useTestSsoConnection() {
  return useMutation({ mutationFn: testSsoConnection });
}

/** QL-637 — the recorded detail of a failed (test) sign-in; null when unknown or expired. */
export function useSsoSignInError(ref: string | null) {
  return useQuery({
    queryKey: ["sso-sign-in-error", ref],
    queryFn: async () => {
      try {
        return await getSsoSignInError(ref as string);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: ref !== null && isSsoSignInErrorRef(ref),
    retry: false,
    staleTime: Infinity, // immutable once recorded
  });
}
